import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Gamepad2Icon, Percent, Loader2 } from 'lucide-react';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

/** معدل نجاح مرجّح من byGame عند غياب overallSuccessRatePercent من الـ API */
function computeOverallSuccessRatePercent(metrics) {
  if (!metrics) return null;
  const direct = metrics.overallSuccessRatePercent ?? metrics.overallSuccessPercent;
  if (direct != null && !Number.isNaN(Number(direct))) {
    return Number(direct);
  }
  const list = metrics.byGame;
  if (!Array.isArray(list) || list.length === 0) return null;
  let weighted = 0;
  let sessions = 0;
  for (const row of list) {
    const ps = Number(row.playSessions) || 0;
    const sr = Number(row.successRatePercent);
    if (ps <= 0 || Number.isNaN(sr)) continue;
    weighted += ps * sr;
    sessions += ps;
  }
  if (sessions <= 0) return null;
  return weighted / sessions;
}

const VARIANTS = {
  amber: {
    card: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50/50 ring-amber-500/10',
    glow: 'from-amber-400/20',
    icon: 'bg-amber-500/15 text-amber-900 ring-amber-400/30',
    label: 'text-amber-950/80',
    val: 'text-amber-950',
  },
  emerald: {
    card: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 ring-emerald-500/10',
    glow: 'from-emerald-400/20',
    icon: 'bg-emerald-500/15 text-emerald-800 ring-emerald-400/30',
    label: 'text-emerald-950/80',
    val: 'text-emerald-950',
  },
};

function SummaryTile({ icon: Icon, label, value, variant }) {
  const v = VARIANTS[variant] || VARIANTS.amber;
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 shadow-md ring-1 transition hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}>
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`} />
      <div className="relative">
        {Icon && (
          <span className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1 text-2xl font-black tabular-nums tracking-tight ${v.val}`}>{value}</p>
      </div>
    </div>
  );
}

/**
 * إحصائيات الألعاب — /education/student-dashboard/games
 */
const StudentDashboardGames = ({ data, loading, isRTL }) => {
  const { t } = useTranslation();

  const gm = data?.metrics;
  const period = data?.period;
  const periodLabel =
    period?.seasonYearLabel || (period?.start && period?.end ? `${period.start} → ${period.end}` : null);

  const rows = Array.isArray(gm?.byGame) ? gm.byGame : [];

  const overallSuccess = useMemo(() => computeOverallSuccessRatePercent(gm), [gm]);

  return (
    <section id="student-dashboard-games" className="scroll-mt-24 overflow-visible rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
      <div className="overflow-hidden rounded-t-[1.2rem] border-b border-amber-200/50 bg-gradient-to-r from-amber-500/15 via-orange-50/80 to-yellow-50/50 px-4 py-3 sm:px-5">
        <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/35 ring-2 ring-white/50">
            <Gamepad2Icon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-black tracking-tight text-amber-950 sm:text-lg">{t('dashboard.stats.sectionGames')}</h2>
            {periodLabel && <p className="mt-0.5 truncate text-xs font-semibold text-amber-900/75">{periodLabel}</p>}
          </div>
        </div>
      </div>
      <div className="relative p-4 sm:p-5">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-[1.2rem] bg-white/75 backdrop-blur-[2px]">
            <Loader2 className="h-10 w-10 animate-spin text-amber-600" strokeWidth={2.5} aria-hidden />
          </div>
        )}
        {gm ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <SummaryTile
                icon={Gamepad2Icon}
                label={t('dashboard.stats.totalPlaySessions')}
                value={fmt(gm.totalPlaySessions)}
                variant="amber"
              />
              <SummaryTile
                icon={Percent}
                label={t('dashboard.stats.overallSuccessRate')}
                value={overallSuccess != null ? pct(overallSuccess) : '—'}
                variant="emerald"
              />
            </div>

            {rows.length > 0 && (
              <div className="mt-8">
                <h3 className="mb-3 inline-flex rounded-lg bg-amber-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950 ring-1 ring-amber-300/50">
                  {t('dashboard.stats.perGameBreakdown')}
                </h3>
                <div className="mt-4 overflow-x-auto rounded-xl border border-amber-200/70 bg-white shadow-md ring-1 ring-amber-500/10">
                  <table className="w-full min-w-[280px] text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-amber-600 to-orange-600 text-[11px] font-bold uppercase tracking-wide text-white">
                        <th className={`py-2.5 ps-4 pe-2 ${isRTL ? 'text-end' : 'text-start'}`}>
                          {t('dashboard.stats.gameIdCol')}
                        </th>
                        <th className="py-2.5 px-2 text-end">{t('dashboard.stats.playSessionsCol')}</th>
                        <th className="py-2.5 pe-4 ps-2 text-end">{t('dashboard.stats.successRateCol')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {rows.map((row, idx) => (
                        <tr
                          key={row.gameId ?? idx}
                          className={`transition-colors hover:bg-amber-50/90 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}`}
                        >
                          <td className={`py-2.5 ps-4 pe-2 font-semibold text-amber-950 ${isRTL ? 'text-end' : 'text-start'}`}>
                            {row.gameName || '—'}
                          </td>
                          <td className="py-2.5 px-2 text-end font-medium tabular-nums text-slate-800">{fmt(row.playSessions)}</td>
                          <td className="py-2.5 pe-4 ps-2 text-end font-bold tabular-nums text-emerald-700">
                            {pct(row.successRatePercent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          !loading && <p className="text-sm font-semibold text-slate-500">{t('users.students.stats.noData')}</p>
        )}
      </div>
    </section>
  );
};

export default StudentDashboardGames;
